import {
  ArrowRight, FileText, Download, ShieldCheck, Link2, Globe,
  Target, Telescope, Shield, Search, Zap, TrendingUp, Layers,
  Cpu, RefreshCw, Scale, Vote, ClipboardList, Lock, Building2,
  Landmark, Radio, MapPin, X, Linkedin, Twitter, Mail,
} from "lucide-react";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal, AnimatedCounter } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATS = [
  { v: 50, s: "+", label: "Asset Classes Supported" },
  { v: 12, s: "+", label: "Countries Represented" },
  { v: 99.9, s: "%", label: "Platform Uptime" },
  { v: 24, s: "+", label: "Core Team Members" },
];

const VALUES = [
  { I: Shield, t: "Security", d: "Security-first infrastructure with robust blockchain standards and operational best practices protecting every layer of the platform." },
  { I: Search, t: "Transparency", d: "Every on-chain transaction is verifiable, creating greater trust, accountability, and confidence for all participants." },
  { I: Zap, t: "Innovation", d: "Continuous development of advanced blockchain solutions purpose-built for Real World Asset tokenization." },
  { I: TrendingUp, t: "Scalability", d: "Infrastructure designed to support long-term ecosystem growth and institutional-grade adoption at scale." },
];

const TIMELINE = [
  { y: "2023 — Foundation", t: "Company Formation & Vision Definition", d: "AUVRA RWA was established with a clear mandate: create institutional-grade infrastructure for Real World Asset tokenization. Core team assembled and initial architecture designed on BNB Smart Chain." },
  { y: "2024 Q1–Q2 — Infrastructure Build", t: "Platform Development & Security Framework", d: "Core smart contract systems developed. Security architecture, compliance frameworks, and asset tokenization protocols engineered to institutional standards. Headquarters established." },
  { y: "2024 Q3–Q4 — Expansion", t: "Team Growth & Ecosystem Foundation", d: "Leadership and operational teams expanded. Board of Directors formalized. Early partnerships initiated. Platform modules for asset issuance, management, and governance advanced." },
  { y: "2025 — Institutional Launch", t: "Full Platform Activation", d: "AUVRA RWA platform opened for institutional and ecosystem participants. Focus on secure tokenization workflows, transparent operations, and scalable digital asset infrastructure." },
  { y: "2026 & Beyond — Global Scale", t: "Ecosystem Maturity & Global Reach", d: "Continued enhancement of technology, expansion of supported asset classes, strengthening of governance mechanisms, and broader institutional adoption across regions." },
];

const REGIONS = [
  { flag: "Europe", h: "Europe", d: "Primary operational base with institutional focus on regulatory alignment, real estate tokenization, and enterprise partnerships.", tag: "Headquarters Region" },
  { flag: "APAC", h: "Asia-Pacific", d: "Growing ecosystem engagement supporting digital asset adoption, blockchain innovation, and cross-border participation.", tag: "Expansion Focus" },
  { flag: "Global", h: "Americas & Global", d: "Strategic outreach for institutional capital, technology partnerships, and broader ecosystem accessibility.", tag: "Network Growth" },
];

const TECH = [
  { I: Link2, t: "BNB Smart Chain", d: "High-performance L1 delivering low latency, low fees, and EVM compatibility for seamless smart contract deployment." },
  { I: ClipboardList, t: "Smart Contract Automation", d: "Automated blockchain processes that improve transparency, efficiency, and operational reliability across the asset lifecycle." },
  { I: Layers, t: "Modular Architecture", d: "Scalable technology stack designed for continuous enhancement, multi-asset support, and institutional integration." },
  { I: RefreshCw, t: "Digital Asset Lifecycle", d: "End-to-end support for issuance, management, governance, and utility of tokenized real-world assets." },
];

const TECH_STATS = [
  { n: "<3s", l: "Average Finality" },
  { n: "Low", l: "Transaction Costs" },
  { n: "EVM", l: "Compatible" },
  { n: "24/7", l: "On-Chain Ops" },
];

const SECURITY = [
  { I: Lock, t: "Smart Contract Security", d: "Rigorous development practices, formal review processes, and continuous monitoring to safeguard on-chain logic and asset controls." },
  { I: ShieldCheck, t: "Operational Security", d: "Multi-layered access controls, secure key management principles, and institutional operational protocols protecting platform integrity." },
  { I: Radio, t: "Infrastructure Resilience", d: "High-availability design, redundant systems, and continuous monitoring ensuring platform reliability and rapid incident response." },
];

const COMPLIANCE = [
  { I: Scale, t: "Regulatory Alignment", d: "Proactive approach to compliance frameworks, transparency standards, and responsible innovation in blockchain-powered finance." },
  { I: Vote, t: "Community Governance", d: "Mechanisms enabling eligible participants to contribute to ecosystem development, protocol improvements, and strategic initiatives." },
  { I: ClipboardList, t: "Transparent Operations", d: "On-chain verifiability and clear operational processes fostering trust among institutional and ecosystem participants." },
  { I: ShieldCheck, t: "Risk Management", d: "Structured risk frameworks and continuous oversight supporting sustainable platform operations and asset integrity." },
];

const WHY = [
  ["01", "Institutional-Grade Architecture", "Enterprise security, compliance readiness, and scalable systems built for professional capital."],
  ["02", "Built on BNB Smart Chain", "Fast, cost-efficient, and battle-tested infrastructure supporting reliable on-chain operations."],
  ["03", "Transparent Infrastructure", "Verifiable on-chain activity and clear operational processes that build lasting trust."],
  ["04", "Utility-Driven Ecosystem", "A comprehensive platform supporting issuance, management, governance, and real utility."],
  ["05", "Secure Smart Contracts", "Robust framework designed to protect assets and automate critical processes with confidence."],
  ["06", "Scalable Technology Stack", "Architecture engineered for long-term growth and institutional adoption at scale."],
  ["07", "Global Ecosystem Vision", "Designed for borderless participation and cross-regional real-world asset access."],
  ["08", "Community-Focused Governance", "Inclusive mechanisms that empower stakeholders to shape the platform's evolution."],
];

const ROADMAP = [
  { p: "Phase 01 — Live", t: "Foundation", items: ["Core platform launch", "Smart contract framework", "Security architecture", "Team & governance setup"], active: true },
  { p: "Phase 02", t: "Expansion", items: ["Multi-asset support", "Partner integrations", "Enhanced governance", "Regional growth"] },
  { p: "Phase 03", t: "Scale", items: ["Institutional products", "Advanced automation", "Cross-chain readiness", "Ecosystem incentives"] },
  { p: "Phase 04", t: "Maturity", items: ["Global network effects", "Full lifecycle tools", "Regulatory leadership", "Sustainable growth"] },
];

const BOARD_BIOS = [
  { name: "Alexander Volkov", role: "Chairman of the Board", bio: "Former head of institutional markets at a European bank. 20+ years across capital markets, custody and structured products.", email: "avolkov@auvrarwa.com" },
  { name: "Isabelle Laurent", role: "Independent Director · Compliance", bio: "Former regulator. Expert in cross-border securities regulation, AML/KYC frameworks and virtual asset service provider oversight.", email: "ilaurent@auvrarwa.com" },
  { name: "Dr. Hiroshi Tanaka", role: "Independent Director · Technology", bio: "Blockchain and distributed systems researcher. Advises central banks and market-infrastructure providers on tokenization architectures.", email: "htanaka@auvrarwa.com" },
  { name: "Sofia Ramirez", role: "Independent Director · Risk", bio: "Risk executive with deep expertise in operational risk, custody and treasury management for global asset managers.", email: "sramirez@auvrarwa.com" },
];

const TEAM_BIOS = [
  { name: "Marcus Berg", role: "Chief Executive Officer", bio: "Founder of AUVRA. Prior: institutional coverage and product roles across capital markets and fintech.", email: "mberg@auvrarwa.com" },
  { name: "Elena Petrova", role: "Chief Operating Officer", bio: "Operations and platform delivery. Prior: COO at institutional custody providers and asset managers.", email: "epetrova@auvrarwa.com" },
  { name: "David Chen", role: "Chief Technology Officer", bio: "Distributed systems, smart contracts and on-chain infrastructure engineering.", email: "dchen@auvrarwa.com" },
  { name: "Anna Kowalski", role: "Chief Compliance Officer", bio: "Compliance, AML/KYC, cross-border regulation and institutional onboarding.", email: "akowalski@auvrarwa.com" },
  { name: "Rafael Costa", role: "Head of Investor Relations", bio: "Institutional relationships, family offices and asset originators.", email: "rcosta@auvrarwa.com" },
  { name: "Priya Sharma", role: "Head of Product", bio: "Investor UX, dashboard product surface and institutional integrations.", email: "psharma@auvrarwa.com" },
];

const FAQ = [
  { q: "What is AUVRA RWA?", a: "AUVRA RWA is a blockchain-powered Real World Asset platform focused on bringing tangible assets onto the blockchain through secure, transparent, and efficient tokenization. It combines modern blockchain infrastructure with institutional standards to enable compliant digital asset ecosystems." },
  { q: "What blockchain does AUVRA use?", a: "AUVRA is built on BNB Smart Chain (BSC), leveraging its fast transaction speeds, low network costs, smart contract functionality, and mature ecosystem to deliver reliable and scalable infrastructure for Real World Asset tokenization." },
  { q: "What types of assets can be tokenized?", a: "The platform is designed to support a range of Real World Assets including real estate, financial instruments, and other tangible or financial assets that can be represented as blockchain-based digital tokens under appropriate frameworks." },
  { q: "How does AUVRA approach security?", a: "Security is foundational. AUVRA employs a multi-layered architecture covering smart contract security, operational controls, secure key management principles, infrastructure resilience, and continuous monitoring to protect digital assets and platform operations." },
  { q: "Is there a governance model?", a: "Yes. The AUVRA ecosystem is designed to support community participation through governance mechanisms that enable eligible participants to contribute to ecosystem development, protocol improvements, and strategic initiatives while maintaining institutional oversight." },
  { q: "Who is AUVRA designed for?", a: "AUVRA serves institutional participants, asset originators, technology partners, and qualified ecosystem participants seeking secure, transparent, and compliant infrastructure for Real World Asset tokenization and digital asset management." },
];

export default function About({ onCta }) {
  const [bio, setBio] = useState(null);
  return (
    <>
      <Navbar onCta={onCta} />
      <main className="relative overflow-hidden">
        {/* HERO */}
        <section className="pt-40 pb-24 relative">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] blue-glow pointer-events-none" />
          <div className="container-auvra relative z-10 text-center max-w-4xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-xs tracking-[0.2em] uppercase text-white/80 mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-auvra-blue animate-pulse" />
                Institutional Infrastructure Live
              </span>
            </Reveal>
            <Reveal i={1}>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]">
                Institutional Infrastructure for<br />
                <span className="bg-gradient-to-r from-auvra-blue via-white to-auvra-blue bg-clip-text text-transparent">
                  Tokenized Real World Assets
                </span>
              </h1>
            </Reveal>
            <Reveal i={2}>
              <p className="mt-8 text-lg text-secondary-auvra leading-relaxed max-w-3xl mx-auto">
                AUVRA RWA is a blockchain-powered Real World Asset platform focused on bringing tangible assets onto the blockchain through secure, transparent, and efficient tokenization. Combining modern blockchain infrastructure with institutional standards for global participation.
              </p>
            </Reveal>
            <Reveal i={3}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <a
                  href="/docs/AUVRA-RWA-Whitepaper.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whitepaper-download-hero"
                  className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]"
                >
                  <FileText className="w-4 h-4" /> Read Whitepaper
                </a>
                <a
                  href="/docs/AUVRA-Institutional-Master-Manual.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="manual-download-hero"
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3 rounded-full transition-all hover:bg-white/5"
                >
                  <Download className="w-4 h-4" /> Company Profile
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* STATS */}
        <section className="pb-20">
          <div className="container-auvra">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {STATS.map((s, i) => (
                <Reveal key={s.label} i={i}>
                  <div className="glass rounded-2xl p-6 md:p-8 text-center h-full">
                    <div className="text-4xl md:text-5xl font-semibold tracking-tight text-auvra-blue">
                      <AnimatedCounter value={s.v} suffix={s.s} />
                    </div>
                    <div className="mt-3 text-xs md:text-sm text-secondary-auvra uppercase tracking-wider">{s.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* WHO WE ARE */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <Reveal><span className="kicker mb-4 block">Who We Are</span></Reveal>
              <Reveal i={1}>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
                  Building the Bridge Between Traditional Finance and Blockchain
                </h2>
              </Reveal>
              <Reveal i={2}>
                <p className="mt-6 text-secondary-auvra leading-relaxed">
                  AUVRA RWA is a blockchain-powered Real World Asset platform focused on bringing tangible assets onto the blockchain through secure, transparent, and efficient tokenization. By combining modern blockchain infrastructure with institutional standards, AUVRA enables compliant digital asset ecosystems designed for global participation.
                </p>
              </Reveal>
              <div className="mt-8 space-y-5">
                {[
                  { I: Landmark, t: "Institutional-Grade Architecture", d: "Enterprise security, compliance frameworks, and scalable infrastructure built for professional capital." },
                  { I: Link2, t: "BNB Smart Chain Powered", d: "Leveraging high throughput, low fees, and a mature ecosystem for reliable on-chain operations." },
                  { I: Globe, t: "Global Accessibility", d: "Designed to enable broader participation in tokenized asset ecosystems worldwide." },
                ].map((f, i) => (
                  <Reveal key={f.t} i={i}>
                    <div className="flex gap-4">
                      <div className="w-11 h-11 flex-shrink-0 rounded-xl glass flex items-center justify-center">
                        <f.I className="w-5 h-5 text-auvra-blue" />
                      </div>
                      <div>
                        <h4 className="font-medium">{f.t}</h4>
                        <p className="text-sm text-secondary-auvra mt-1">{f.d}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal i={2}>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[4/5]">
                  <img src="/about/1000647732.jpg" alt="AUVRA Corporate Headquarters" className="w-full h-full object-cover" />
                </div>
                <div className="glass absolute bottom-6 -left-4 md:-left-8 max-w-xs rounded-2xl p-5 shadow-xl">
                  <h5 className="font-medium">Corporate Headquarters</h5>
                  <p className="text-xs text-secondary-auvra mt-1">Modern institutional offices designed for collaboration, security, and operational excellence.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </Section>

        {/* MISSION & VISION */}
        <Section>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { I: Target, t: "Our Mission", d: "Our mission is to make Real World Assets more accessible, transparent, and efficient through blockchain technology while providing institutional-grade infrastructure that connects traditional finance with decentralized innovation." },
              { I: Telescope, t: "Our Vision", d: "To become a global leader in Real World Asset tokenization by building trusted digital infrastructure that enables secure ownership, seamless asset management, and sustainable long-term value creation." },
            ].map((c, i) => (
              <Reveal key={c.t} i={i}>
                <div className="glass rounded-2xl p-10 h-full">
                  <div className="w-14 h-14 rounded-2xl bg-auvra-blue/10 border border-auvra-blue/20 flex items-center justify-center mb-6">
                    <c.I className="w-6 h-6 text-auvra-blue" />
                  </div>
                  <h3 className="text-2xl font-semibold tracking-tight mb-3">{c.t}</h3>
                  <p className="text-secondary-auvra leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* VALUES */}
        <Section
          center
          kicker="Platform Principles"
          title="Core Values That Guide Everything We Build"
          sub="Security, transparency, innovation, and scalability form the foundation of the AUVRA ecosystem."
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} i={i % 4}>
                <div className="glass rounded-2xl p-8 h-full hover:border-auvra-blue/30 transition-colors">
                  <v.I className="w-6 h-6 text-auvra-blue mb-5" />
                  <h4 className="font-medium mb-2">{v.t}</h4>
                  <p className="text-sm text-secondary-auvra leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* TIMELINE */}
        <Section kicker="Our Journey" title="The AUVRA Story" sub="From vision to institutional infrastructure — key milestones in building the future of tokenized real-world assets.">
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-auvra-blue/40 via-white/10 to-transparent" />
            <div className="space-y-10">
              {TIMELINE.map((it, i) => (
                <Reveal key={it.y} i={i}>
                  <div className={`grid md:grid-cols-2 gap-6 md:gap-16 items-start ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}>
                    <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? "md:text-right md:pr-8" : "md:pl-8"} relative`}>
                      <div className="absolute left-2 md:left-auto md:right-[-6px] top-2 w-3 h-3 rounded-full bg-auvra-blue ring-4 ring-black shadow-[0_0_20px_rgba(37,99,235,0.6)]" style={i % 2 === 1 ? { right: "auto", left: "-6px" } : {}} />
                      <div className="text-xs tracking-[0.2em] uppercase text-auvra-blue mb-2">{it.y}</div>
                      <h4 className="text-xl font-semibold tracking-tight">{it.t}</h4>
                    </div>
                    <div className={`pl-12 md:pl-0 ${i % 2 === 0 ? "md:pl-8" : "md:text-right md:pr-8"}`}>
                      <p className="text-secondary-auvra leading-relaxed text-sm">{it.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        {/* BOARD */}
        <Section kicker="Governance" title="Board of Directors" sub="Experienced leaders guiding strategic direction, risk oversight, and institutional integrity.">
          <Reveal>
            <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
              <img src="/about/1000645149.jpg" alt="AUVRA Board of Directors" className="w-full h-[420px] md:h-[520px] object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">Board of Directors</h3>
                <p className="text-secondary-auvra mt-2 max-w-2xl">Committed to institutional standards, transparent governance, and long-term value creation for the AUVRA ecosystem.</p>
              </div>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="board-bios">
            {BOARD_BIOS.map((b, i) => (
              <Reveal key={b.name} i={i % 4}>
                <button onClick={() => setBio(b)} data-testid={`board-${b.name.split(" ")[0]}`} className="text-left w-full glass rounded-2xl p-6 hover:border-auvra-blue/30 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-auvra-blue/20 border border-auvra-blue/30 flex items-center justify-center text-lg font-semibold text-auvra-blue mb-4">{b.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-secondary-auvra mt-1">{b.role}</div>
                  <div className="text-[10px] text-auvra-blue mt-3 inline-flex items-center gap-1">Read bio <ArrowRight className="w-3 h-3" /></div>
                </button>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* EXECUTIVE LEADERSHIP & GLOBAL TEAM */}
        <Section kicker="Our People" title="Executive Leadership & Global Team" sub="A multidisciplinary team of blockchain engineers, finance professionals, compliance specialists, and operations experts united by a shared vision.">
          <div className="grid gap-6">
            <Reveal>
              <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
                <img src="/about/1000645148.jpg" alt="AUVRA Leadership and Staff" className="w-full h-[380px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">Together We Build · Together We Grow · Together We Thrive</h3>
                  <p className="text-secondary-auvra mt-2 max-w-2xl">The AUVRA team combines deep expertise in traditional finance, blockchain technology, and institutional operations.</p>
                </div>
              </div>
            </Reveal>
            <Reveal i={1}>
              <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
                <img src="/about/1000647730.jpg" alt="AUVRA Team at Headquarters" className="w-full h-[380px] md:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-tight">Global Team</h3>
                  <p className="text-secondary-auvra mt-2 max-w-2xl">Dedicated professionals driving the future of Real World Asset tokenization from our corporate headquarters and beyond.</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="team-bios">
            {TEAM_BIOS.map((b, i) => (
              <Reveal key={b.name} i={i % 3}>
                <button onClick={() => setBio(b)} data-testid={`team-${b.name.split(" ")[0]}`} className="text-left w-full glass rounded-2xl p-6 hover:border-auvra-blue/30 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-auvra-blue/20 border border-auvra-blue/30 flex items-center justify-center text-lg font-semibold text-auvra-blue mb-4">{b.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                  <div className="font-medium">{b.name}</div>
                  <div className="text-xs text-secondary-auvra mt-1">{b.role}</div>
                  <div className="text-[10px] text-auvra-blue mt-3 inline-flex items-center gap-1">Read bio <ArrowRight className="w-3 h-3" /></div>
                </button>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* HQ */}
        <Section kicker="Our Home" title="Corporate Headquarters" sub="A modern institutional workspace designed for collaboration, security, and operational excellence.">
          <div className="grid md:grid-cols-2 gap-4">
            <Reveal>
              <div className="rounded-2xl overflow-hidden border border-white/10 h-full">
                <img src="/about/1000647732.jpg" alt="AUVRA HQ Exterior" className="w-full h-full object-cover min-h-[420px]" />
              </div>
            </Reveal>
            <div className="grid grid-rows-2 gap-4">
              <Reveal i={1}>
                <div className="rounded-2xl overflow-hidden border border-white/10 h-full">
                  <img src="/about/1000647731.jpg" alt="AUVRA Office Entrance" className="w-full h-full object-cover min-h-[200px]" />
                </div>
              </Reveal>
              <Reveal i={2}>
                <div className="rounded-2xl overflow-hidden border border-white/10 h-full">
                  <img src="/about/1000647730.jpg" alt="AUVRA Team at Entrance" className="w-full h-full object-cover min-h-[200px]" />
                </div>
              </Reveal>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { t: "Modern Facilities", d: "Purpose-built offices supporting secure operations, client engagement, and team collaboration." },
              { t: "Institutional Presence", d: "Professional environment reflecting the standards expected by institutional partners and stakeholders." },
              { t: "Strategic Location", d: "Positioned to support regional operations and connectivity with global financial and technology ecosystems." },
            ].map((c, i) => (
              <Reveal key={c.t} i={i}>
                <div className="glass rounded-2xl p-6 h-full">
                  <Building2 className="w-5 h-5 text-auvra-blue mb-3" />
                  <h4 className="font-medium mb-2">{c.t}</h4>
                  <p className="text-sm text-secondary-auvra">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* GLOBAL */}
        <Section center kicker="Worldwide Reach" title="Global Presence" sub="Building a borderless infrastructure for tokenized real-world assets with strategic focus across key regions.">
          <div className="grid md:grid-cols-3 gap-4">
            {REGIONS.map((r, i) => (
              <Reveal key={r.h} i={i}>
                <div className="glass rounded-2xl p-8 h-full hover:border-auvra-blue/30 transition-colors">
                  <MapPin className="w-6 h-6 text-auvra-blue mb-5" />
                  <h4 className="text-lg font-semibold tracking-tight mb-2">{r.h}</h4>
                  <p className="text-sm text-secondary-auvra leading-relaxed mb-4">{r.d}</p>
                  <span className="inline-block text-xs tracking-wider uppercase px-3 py-1 rounded-full bg-auvra-blue/10 border border-auvra-blue/20 text-auvra-blue">{r.tag}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* TECHNOLOGY */}
        <Section kicker="Infrastructure" title="Technology Infrastructure" sub="Built on BNB Smart Chain (BSC), AUVRA leverages fast transaction speeds, low network costs, smart contract functionality, and a mature blockchain ecosystem.">
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            <div className="lg:col-span-3 space-y-6">
              {TECH.map((t, i) => (
                <Reveal key={t.t} i={i}>
                  <div className="flex gap-5 items-start">
                    <div className="w-11 h-11 flex-shrink-0 rounded-xl glass flex items-center justify-center">
                      <t.I className="w-5 h-5 text-auvra-blue" />
                    </div>
                    <div>
                      <h4 className="font-medium">{t.t}</h4>
                      <p className="text-sm text-secondary-auvra mt-1 leading-relaxed">{t.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              {TECH_STATS.map((s, i) => (
                <Reveal key={s.l} i={i}>
                  <div className="glass rounded-2xl p-6 text-center">
                    <div className="text-3xl font-semibold text-auvra-blue tracking-tight">{s.n}</div>
                    <div className="text-xs text-secondary-auvra mt-2 uppercase tracking-wider">{s.l}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Section>

        {/* SECURITY */}
        <Section center kicker="Defense in Depth" title="Security Architecture" sub="Advanced security architecture designed to protect digital assets, platform operations, and stakeholder interests at every layer.">
          <div className="grid md:grid-cols-3 gap-4">
            {SECURITY.map((s, i) => (
              <Reveal key={s.t} i={i}>
                <div className="glass rounded-2xl p-8 h-full">
                  <s.I className="w-6 h-6 text-auvra-blue mb-5" />
                  <h4 className="font-medium mb-2">{s.t}</h4>
                  <p className="text-sm text-secondary-auvra leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* COMPLIANCE */}
        <Section kicker="Trust Framework" title="Compliance & Governance" sub="The AUVRA ecosystem is designed to support community participation through governance mechanisms while maintaining institutional standards of oversight and accountability.">
          <div className="grid md:grid-cols-2 gap-4">
            {COMPLIANCE.map((c, i) => (
              <Reveal key={c.t} i={i}>
                <div className="glass rounded-2xl p-8 h-full flex gap-5 items-start">
                  <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-auvra-blue/10 border border-auvra-blue/20 flex items-center justify-center">
                    <c.I className="w-5 h-5 text-auvra-blue" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">{c.t}</h4>
                    <p className="text-sm text-secondary-auvra leading-relaxed">{c.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* WHY CHOOSE */}
        <Section center kicker="Differentiation" title="Why Choose AUVRA" sub="Institutional-grade architecture combined with a clear vision for the future of tokenized real-world assets.">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {WHY.map(([n, t, d], i) => (
              <Reveal key={n} i={i % 4}>
                <div className="glass rounded-2xl p-6 h-full hover:border-auvra-blue/30 transition-colors">
                  <div className="text-xs tracking-[0.2em] text-auvra-blue mb-3">{n}</div>
                  <h4 className="font-medium mb-2">{t}</h4>
                  <p className="text-sm text-secondary-auvra leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* ROADMAP */}
        <Section center kicker="Looking Ahead" title="Roadmap" sub="AUVRA is focused on advancing the adoption of tokenized Real World Assets through continuous technology enhancement and responsible innovation.">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ROADMAP.map((r, i) => (
              <Reveal key={r.t} i={i}>
                <div className={`glass rounded-2xl p-6 h-full border ${r.active ? "border-auvra-blue/40 shadow-[0_0_40px_rgba(37,99,235,0.15)]" : "border-white/5"}`}>
                  <div className={`text-xs tracking-[0.2em] uppercase mb-3 ${r.active ? "text-auvra-blue" : "text-secondary-auvra"}`}>{r.p}</div>
                  <h4 className="text-lg font-semibold tracking-tight mb-4">{r.t}</h4>
                  <ul className="space-y-2 text-sm text-secondary-auvra">
                    {r.items.map((x) => (
                      <li key={x} className="flex gap-2"><span className="text-auvra-blue">›</span>{x}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* RESOURCES */}
        <Section kicker="Institutional Documentation" title="Whitepaper & Company Profile" sub="Download the AUVRA RWA institutional documentation set — including the complete whitepaper and institutional master manual outlining the platform, governance, and infrastructure.">
          <div className="grid md:grid-cols-2 gap-4">
            <Reveal>
              <a
                href="/docs/AUVRA-RWA-Whitepaper.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whitepaper-download"
                className="group glass rounded-2xl p-8 h-full flex flex-col justify-between hover:border-auvra-blue/40 transition-all"
              >
                <div>
                  <div className="w-12 h-12 rounded-xl bg-auvra-blue/10 border border-auvra-blue/20 flex items-center justify-center mb-6">
                    <FileText className="w-5 h-5 text-auvra-blue" />
                  </div>
                  <div className="kicker mb-2">Whitepaper</div>
                  <h4 className="text-2xl font-semibold tracking-tight mb-3">AUVRA RWA Whitepaper</h4>
                  <p className="text-sm text-secondary-auvra leading-relaxed">The complete technical and strategic overview of the AUVRA Real World Asset tokenization platform, architecture, and ecosystem.</p>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-sm text-auvra-blue">
                  Open Whitepaper <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </a>
            </Reveal>
            <Reveal i={1}>
              <a
                href="/docs/AUVRA-Institutional-Master-Manual.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="manual-download"
                className="group glass rounded-2xl p-8 h-full flex flex-col justify-between hover:border-auvra-blue/40 transition-all"
              >
                <div>
                  <div className="w-12 h-12 rounded-xl bg-auvra-blue/10 border border-auvra-blue/20 flex items-center justify-center mb-6">
                    <Download className="w-5 h-5 text-auvra-blue" />
                  </div>
                  <div className="kicker mb-2">Company Profile</div>
                  <h4 className="text-2xl font-semibold tracking-tight mb-3">Institutional Master Manual</h4>
                  <p className="text-sm text-secondary-auvra leading-relaxed">A comprehensive institutional profile of AUVRA — governance, compliance, technology infrastructure, and operational excellence.</p>
                </div>
                <div className="mt-6 inline-flex items-center gap-2 text-sm text-auvra-blue">
                  Open Master Manual <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </div>
              </a>
            </Reveal>
          </div>
        </Section>

        {/* FAQ */}
        <Section center kicker="Knowledge Base" title="Frequently Asked Questions" sub="Clear answers to common questions about AUVRA RWA and Real World Asset tokenization.">
          <div className="max-w-3xl mx-auto space-y-3">
            {FAQ.map((f, i) => (
              <Reveal key={f.q} i={i}>
                <details className="group glass rounded-xl p-6 open:border-auvra-blue/30 transition-colors" data-testid={`faq-${i}`}>
                  <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                    <span className="font-medium">{f.q}</span>
                    <span className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center text-auvra-blue transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm text-secondary-auvra leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </Section>

        {/* CTA */}
        <section className="py-24 relative">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-[900px] h-[500px] blue-glow" />
          </div>
          <div className="container-auvra relative z-10 text-center max-w-3xl">
            <Reveal>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
                Join the Future of Real World Asset Tokenization
              </h2>
            </Reveal>
            <Reveal i={1}>
              <p className="mt-6 text-secondary-auvra leading-relaxed">
                Explore the AUVRA ecosystem and discover how blockchain technology is transforming the future of digital assets with institutional-grade infrastructure.
              </p>
            </Reveal>
            <Reveal i={2}>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={onCta}
                  data-testid="about-cta-explore"
                  className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]"
                >
                  Explore Platform <ArrowRight className="w-4 h-4" />
                </button>
                <a
                  href="/docs/AUVRA-Institutional-Master-Manual.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-cta-docs"
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white font-medium px-6 py-3 rounded-full transition-all hover:bg-white/5"
                >
                  Read Documentation
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Dialog open={!!bio} onOpenChange={(o) => !o && setBio(null)}>
        <DialogContent className="bg-auvra-elevated border border-white/10 text-white sm:max-w-md" data-testid="bio-modal">
          {bio && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-auvra-blue/20 border border-auvra-blue/30 flex items-center justify-center text-lg font-semibold text-auvra-blue">{bio.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</div>
                <div><div>{bio.name}</div><div className="text-xs text-secondary-auvra font-normal">{bio.role}</div></div>
              </DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2">
                <p className="text-sm text-secondary-auvra leading-relaxed">{bio.bio}</p>
                <div className="flex gap-3 pt-3 border-t border-white/5">
                  <a href={`mailto:${bio.email}`} className="inline-flex items-center gap-2 text-sm border border-white/15 hover:border-auvra-blue rounded-full px-4 py-2 transition-colors"><Mail className="w-4 h-4" /> Email</a>
                  <a href="#" onClick={(e) => e.preventDefault()} className="inline-flex items-center gap-2 text-sm border border-white/15 hover:border-auvra-blue rounded-full px-4 py-2 transition-colors"><Linkedin className="w-4 h-4" /> LinkedIn</a>
                  <a href="#" onClick={(e) => e.preventDefault()} className="inline-flex items-center gap-2 text-sm border border-white/15 hover:border-auvra-blue rounded-full px-4 py-2 transition-colors"><Twitter className="w-4 h-4" /> Twitter</a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </>
  );
}

function Section({ children, kicker, title, sub, center = false }) {
  return (
    <section className="py-20 md:py-24">
      <div className="container-auvra">
        {(kicker || title) && (
          <div className={`mb-14 ${center ? "text-center max-w-2xl mx-auto" : "max-w-3xl"}`}>
            {kicker && <Reveal><span className="kicker mb-4 block">{kicker}</span></Reveal>}
            {title && (
              <Reveal i={1}>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">{title}</h2>
              </Reveal>
            )}
            {sub && (
              <Reveal i={2}>
                <p className="mt-6 text-secondary-auvra leading-relaxed">{sub}</p>
              </Reveal>
            )}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}
